import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ListChecks, Cog, SlidersHorizontal, UserCheck, BadgeCheck } from 'lucide-react';

const OurPromise = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const pillars = [
    {
      icon: ListChecks,
      title: 'Task Management',
      description: 'Every task tracked, prioritised, and delivered on time — nothing slips through the cracks.'
    },
    {
      icon: Cog,
      title: 'Systems',
      description: 'We work within your existing tools and processes for a seamless, integrated experience.'
    },
    {
      icon: SlidersHorizontal,
      title: "You're Always in Control",
      description: 'Full visibility and oversight of your operations, with the freedom to adjust anytime.'
    },
    {
      icon: UserCheck,
      title: 'Experienced Remote Administrator',
      description: 'A dedicated, skilled professional who understands your business inside out.'
    },
    {
      icon: BadgeCheck,
      title: 'Quality Assured',
      description: 'Rigorous standards and reviews ensure consistently accurate, high-quality work.'
    }
  ];

  return (
    <section id="promise" ref={ref} className="relative py-32 bg-white overflow-hidden">
      {/* Decorative */}

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-block px-4 py-2 bg-gradient-to-r from-yellow-100 to-blue-100 text-yellow-700 rounded-full text-sm font-semibold mb-6">
            OUR PROMISE
          </div>
          <h2 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
            Admin Support
            <span className="block bg-gradient-to-r from-yellow-600 via-blue-600 to-yellow-600 bg-clip-text text-transparent leading-tight pb-2">Tailored To Suit You!</span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            You can rest assured, all your administrative tasks are completely taken care of here&hellip;
          </p>
        </motion.div>

        {/* Pillars Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {pillars.map((pillar, index) => {
            const Icon = pillar.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                data-testid={`promise-pillar-${index}`}
                className="group p-8 bg-slate-50 rounded-3xl border border-slate-100 hover:border-yellow-400/40 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-slate-900 to-blue-950 rounded-2xl flex items-center justify-center mb-6 group-hover:from-yellow-500 group-hover:to-yellow-600 transition-all duration-500">
                  <Icon className="w-8 h-8 text-yellow-400 group-hover:text-slate-900 transition-colors duration-500" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">{pillar.title}</h3>
                <p className="text-slate-600 leading-relaxed">{pillar.description}</p>
              </motion.div>
            );
          })}

          {/* CTA card filling the grid */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
            transition={{ duration: 0.6, delay: pillars.length * 0.1 }}
            className="p-8 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 rounded-3xl flex flex-col justify-center items-start shadow-2xl"
          >
            <h3 className="text-2xl font-bold text-white mb-3">Ready when you are</h3>
            <p className="text-slate-300 mb-6">Let us handle the admin so you can focus on growing your business.</p>
            <button
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="promise-cta"
              className="px-6 py-3 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 text-slate-900 font-bold rounded-xl transition-all duration-300 shadow-lg shadow-yellow-500/40 hover:scale-105"
            >
              Get Started
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default OurPromise;
